import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';

// Icons
const LogIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
);

interface AuditLog {
  id: string;
  team_id: string;
  user_id: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  details?: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  user?: {
    username: string;
    display_name?: string;
    first_name?: string;
    last_name?: string;
  };
}

interface TeamAuditLogsTabProps {
  teamId: string;
  permissions: {
    can_view_team: boolean;
    can_view_audit_logs: boolean;
  };
  isActive: boolean;
}

export const TeamAuditLogsTab: React.FC<TeamAuditLogsTabProps> = ({
  teamId,
  permissions,
  isActive,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { colorSchemes } = useThemeContext();
  const teamService = useTeamService();

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');

  // State
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load audit logs data only when tab becomes active
  const loadAuditLogs = async () => {
    if (!teamId || !permissions.can_view_audit_logs) return;

    try {
      setIsLoading(true);
      setError('');
      
      // Note: This API endpoint might not exist yet
      const auditLogsData = await teamService.getTeamAuditLogs?.(teamId);
      setAuditLogs(auditLogsData || []);
      setHasLoaded(true);
    } catch (err: any) {
      console.error('Error loading team audit logs:', err);
      setError(err.message || 'Failed to load team audit logs');
      toast({
        title: t('common.error'),
        description: err.message || 'Failed to load team audit logs',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when tab becomes active
  useEffect(() => {
    if (isActive && !hasLoaded && permissions.can_view_audit_logs) {
      loadAuditLogs();
    }
  }, [isActive, hasLoaded, permissions.can_view_audit_logs, teamId]);

  // Get action badge color
  const getActionBadgeColor = (action: string) => {
    if (action.includes('create')) return 'green';
    if (action.includes('update') || action.includes('edit')) return 'blue';
    if (action.includes('delete') || action.includes('remove')) return 'red';
    if (action.includes('invite') || action.includes('add')) return 'purple';
    return 'gray';
  };

  // Get user display name
  const getUserDisplayName = (log: AuditLog) => {
    if (!log.user) return 'Unknown User';
    if (log.user.display_name) return log.user.display_name;
    if (log.user.first_name && log.user.last_name) {
      return `${log.user.first_name} ${log.user.last_name}`;
    }
    return log.user.username || 'Unknown User';
  };

  // Format action text
  const formatAction = (action: string) => {
    return action
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Permission check
  if (!permissions.can_view_audit_logs) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="warning">
          <AlertIcon />
          {t('teams.noPermissionToViewAuditLogs')}
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading && !hasLoaded) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" />
        <Text>{t('teams.loadingAuditLogs')}</Text>
      </VStack>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={loadAuditLogs} colorScheme={colorSchemes.primary}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  // Empty state
  if (!isLoading && auditLogs.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color={textColor} fontSize="lg">
          {t('teams.noAuditLogs')}
        </Text>
        <Text color={textColor} fontSize="sm" mt={2}>
          {t('teams.auditLogsWillAppearHere')}
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <HStack spacing={2}>
          <LogIcon />
          <Text fontSize="lg" fontWeight="semibold">
            {t('teams.auditLogs')} ({auditLogs.length})
          </Text>
        </HStack>
        <Button
          size="sm"
          onClick={loadAuditLogs}
          isLoading={isLoading}
          colorScheme={colorSchemes.neutral}
          variant="outline"
        >
          {t('common.refresh')}
        </Button>
      </HStack>

      {/* Audit Logs Table */}
      <Card bg={cardBg} borderWidth={1} borderColor={borderColor}>
        <CardBody p={0}>
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('teams.user')}</Th>
                  <Th>{t('teams.action')}</Th>
                  <Th>{t('teams.resource')}</Th>
                  <Th>{t('teams.timestamp')}</Th>
                  <Th>{t('teams.details')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {auditLogs.map((log) => (
                  <Tr key={log.id}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" fontSize="sm">
                          {getUserDisplayName(log)}
                        </Text>
                        {log.user?.username && (
                          <Text fontSize="xs" color={textColor}>
                            @{log.user.username}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={getActionBadgeColor(log.action)}
                        variant="subtle"
                        size="sm"
                      >
                        {formatAction(log.action)}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {log.resource_type}
                        </Text>
                        {log.resource_id && (
                          <Text fontSize="xs" color={textColor}>
                            ID: {log.resource_id}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm">
                          {new Date(log.created_at).toLocaleDateString()}
                        </Text>
                        <Text fontSize="xs" color={textColor}>
                          {new Date(log.created_at).toLocaleTimeString()}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        {log.ip_address && (
                          <Text fontSize="xs" color={textColor}>
                            IP: {log.ip_address}
                          </Text>
                        )}
                        {log.details && typeof log.details === 'object' && (
                          <Text fontSize="xs" color={textColor}>
                            {JSON.stringify(log.details).slice(0, 50)}...
                          </Text>
                        )}
                      </VStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
    </VStack>
  );
}; 